import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

// The people selector drawer's behaviour (#269): filtering, following the
// canvas's job selection, the deferred signals, quick-add.
//
// Unscoped, the rows are what the canvas rendered. Scoped to a job, the list
// is fetched in two halves, the way the canvas chips get their badges: first
// the candidate groups from the plan graph (names paint at once), then the
// cross-plan conflict signals as decorations keyed by selection value. Rows
// grow a context line only when there is something to explain.
//
// Two instances share the page (desktop panel, mobile panel) and both hear
// the canvas. Only the visible one fetches; the hidden one remembers it is
// stale and catches up when it is shown.
//
// Filtering is client-side over the rows present -- no round trip per
// keystroke on the render path the canvas is measured on.
export default class extends Controller {
  static targets = ["search", "effectOption", "planningOption", "title", "header", "list", "empty", "row", "quickAdd"]
  static values = {
    scope: {type: String, default: "desktop"},
    returnTo: String,
    listUrl: String
  }

  connect() {
    // Scope state lives on the instance on purpose: a Turbo morph rewrites
    // every attribute from the server's HTML, which knows nothing of the
    // selection, so a Stimulus value would forget it on the first edit.
    // `job` is what the canvas asked for; `renderedJob` is what the rows on
    // screen belong to. They differ while a load is in flight.
    this.job = this.job || null
    this.renderedJob = this.renderedJob || null
    this.decorated = this.decorated || false
    this.stale = false
    this.unscopedListHTML = this.listTarget.querySelector("[data-drawer-deferred]") ? this.listTarget.innerHTML : ""
    this.unscopedDirty = true

    this.visibility = new IntersectionObserver(() => this.visibilityChanged())
    this.visibility.observe(this.listTarget)

    this.syncScope()
    this.filter()
    this.visibilityChanged()
  }

  disconnect() {
    this.visibility?.disconnect()
    this.abortController?.abort()
    cancelAnimationFrame(this.visibilityFrame)
  }

  // Opening a mobile tab can reveal a list below the viewport. Load on
  // section visibility, without requiring its first row to intersect it.
  visibilityChanged() {
    cancelAnimationFrame(this.visibilityFrame)
    this.visibilityFrame = requestAnimationFrame(() => {
      if (!this.#visible()) return
      if (this.job && this.stale) this.load()
      else if (!this.job && this.unscopedDirty) this.loadUnscoped()
    })
  }

  filter() {
    const term = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()
    const effects = this.effectOptionTargets.filter(option => option.checked).map(option => option.value)
    const categories = new Map()
    this.planningOptionTargets.filter(option => option.checked).forEach(option => {
      const category = option.dataset.drawerPlanningCategoryId
      categories.set(category, [...(categories.get(category) || []), option.value])
    })

    let visible = 0

    this.rowTargets.forEach(row => {
      const matchesTerm = !term || (row.dataset.drawerPersonSearch || "").includes(term)
      // A row with no resolved effect only survives an effect filter if no
      // effect filter is active -- "unknown" is not a match for "positive".
      const matchesEffect = effects.length === 0 || effects.includes(row.dataset.drawerPersonEffect)
      const values = JSON.parse(row.dataset.drawerPersonPlanningValues || "{}")
      const matchesCategories = [...categories].every(([category, selected]) => {
        const assigned = values[category] || []
        return selected.some(value => value === "unset" ? assigned.length === 0 : assigned.includes(value))
      })
      const show = matchesTerm && matchesEffect && matchesCategories

      row.classList.toggle("hidden", !show)
      if (show) visible += 1
    })

    const waitingForRows = this.job ? this.renderedJob?.jobId !== this.job.jobId : this.unscopedDirty
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", visible > 0 || waitingForRows)
  }

  // The canvas selected a job: follow it. Quick-add goes dark until the new
  // rows are in, so a click during the load cannot post the old rows' people
  // to the new job.
  scopeToJob(event) {
    const {jobId, jobPeoplePath, drawerPath, decorationsPath} = event?.detail || {}
    if (!jobId || !drawerPath) return
    if (event.detail.restored && this.job?.jobId === jobId) return

    if (this.job?.jobId !== jobId) {
      // Old candidates remain drag sources even with quick-add hidden. Remove
      // them as soon as the scope changes, while keeping the unscoped snapshot
      // available for Clear scope or recovery from a failed request.
      this.abortController?.abort()
      this.renderedJob = null
      if (this.hasHeaderTarget) {
        this.headerTarget.replaceChildren()
        this.headerTarget.classList.add("hidden")
      }
      if (this.hasListTarget) {
        this.listTarget.replaceChildren()
        this.listTarget.setAttribute("aria-busy", "true")
      }
    }
    this.job = {jobId, jobPeoplePath: jobPeoplePath || "", drawerPath, decorationsPath: decorationsPath || ""}
    this.decorated = false
    this.syncScope()
    this.filter()
    this.load()
  }

  // The drawer's own close control, or the canvas deselecting. One event
  // for both, so the other instance and the canvas ring clear too.
  clearScope(event) {
    const wasScoped = Boolean(this.job || this.renderedJob)
    this.abortController?.abort()
    this.job = null
    this.renderedJob = null
    this.decorated = false
    this.stale = false

    if (this.hasListTarget) {
      this.listTarget.innerHTML = this.unscopedListHTML
      this.listTarget.removeAttribute("aria-busy")
    }
    if (this.hasHeaderTarget) {
      this.headerTarget.innerHTML = ""
      this.headerTarget.classList.add("hidden")
    }
    this.#scrollListToTop()
    this.syncScope()
    this.filter()
    if (this.unscopedDirty) this.loadUnscoped()

    if (wasScoped && event?.type === "click") {
      window.dispatchEvent(new CustomEvent("canvas:job-deselected"))
    }
  }

  // A morph re-rendered the sidebar from the server, which knows nothing of
  // the scope. Whatever we were showing, the list now holds the server's
  // fresh unscoped rows: keep them for the next clear, then fetch the scope
  // again.
  reload(event) {
    if (!this.hasListTarget) return
    // A server refresh invalidates pending data; visibility-only notifications
    // share the existing request instead.
    this.unscopedRequest?.abort()
    if (event?.type === "canvas:authoritative-refresh-complete") this.unscopedDirty = true

    // Inbox and other partial morphs can leave this list untouched. Only a
    // server-rendered deferred placeholder replaces the unscoped snapshot;
    // scoped rows must never become the list restored by Clear job scope.
    if (this.listTarget.querySelector("[data-drawer-deferred]")) {
      this.unscopedListHTML = this.listTarget.innerHTML
      this.unscopedDirty = true
    }
    if (this.job) {
      this.decorated = false
      this.syncScope()
      this.load()
    } else {
      this.filter()
      if (this.unscopedDirty) this.loadUnscoped()
    }
  }

  async loadUnscoped() {
    if (this.job || !this.unscopedDirty || !this.#visible() || !this.hasListUrlValue) return
    if (this.unscopedRequest && !this.unscopedRequest.signal.aborted) return
    this.abortController?.abort()
    const abortController = new AbortController()
    this.abortController = abortController
    this.unscopedRequest = abortController
    const {signal} = abortController
    try {
      const response = await get(this.listUrlValue, {
        query: {scope: this.scopeValue, return_to: this.returnToValue}, responseKind: "html", signal
      })
      if (signal.aborted || this.job) return
      if (!response.ok) throw new Error("Unable to load people")
      const html = await response.html
      if (signal.aborted || this.job) return
      this.listTarget.innerHTML = html
      this.#updatePeopleCount(this.listTarget)
      this.unscopedListHTML = html
      this.unscopedDirty = false
      this.filter()
    } catch (error) {
      if (signal.aborted) return
      this.listTarget.innerHTML = '<button type="button" class="m-3 text-sm underline" data-action="canvas-people-drawer#loadUnscoped">Unable to load people. Retry</button>'
    } finally {
      if (this.unscopedRequest === abortController) this.unscopedRequest = null
    }
  }

  async load() {
    const job = this.job
    if (!job) return
    if (!this.#visible()) {
      this.stale = true
      return
    }
    this.stale = false

    this.abortController?.abort()
    const abortController = new AbortController()
    this.abortController = abortController
    const {signal} = abortController

    let html
    try {
      const response = await get(job.drawerPath, {
        query: {scope: this.scopeValue, return_to: this.returnToValue},
        responseKind: "html",
        signal
      })
      if (signal.aborted) return
      if (!response.ok) throw new Error("Unable to load job candidates")
      html = await response.html
    } catch (error) {
      if (signal.aborted) return
      if (this.renderedJob?.jobId === job.jobId) {
        // A refresh of the scope already on screen can fail after a mutation
        // succeeded. Keep the selected job, but remove stale candidates so
        // they cannot be submitted again while the user retries the read.
        this.decorated = false
        this.syncScope()
        if (this.hasListTarget) {
          this.listTarget.innerHTML = '<button type="button" class="m-3 text-sm underline" data-action="canvas-people-drawer#load">Unable to refresh candidates. Retry</button>'
          this.listTarget.removeAttribute("aria-busy")
        }
      } else {
        // An initial scope change has no trustworthy scoped content to keep.
        this.clearScope()
        window.dispatchEvent(new CustomEvent("canvas:job-deselected"))
      }
      return
    }

    if (signal.aborted || this.job !== job) return
    const parsed = new DOMParser().parseFromString(html, "text/html")
    const header = parsed.querySelector("[data-drawer-part='header']")
    const list = parsed.querySelector("[data-drawer-part='list']")
    this.#updatePeopleCount(parsed)

    if (this.hasHeaderTarget) {
      this.headerTarget.innerHTML = header ? header.innerHTML : ""
      this.headerTarget.classList.toggle("hidden", !header)
    }
    if (this.hasListTarget && list) {
      this.listTarget.innerHTML = list.innerHTML
      this.listTarget.removeAttribute("aria-busy")
    }
    this.#scrollListToTop()

    this.renderedJob = job
    this.decorated = !job.decorationsPath
    this.syncScope()
    this.filter()

    if (job.decorationsPath) {
      const applied = await this.decorate(job.decorationsPath, signal)
      if (signal.aborted) return
      // Quick-add confirms a planning warning from the decoration's own
      // confirmation text, and nothing else writes it. If the request failed,
      // staying undecorated keeps quick-add hidden rather than letting an
      // assignment skip the confirm the picker would have shown.
      this.decorated = applied
      this.syncScope()
    }
  }

  // The expensive half. Applied absolutely: every row is reset first, so a
  // finding that disappeared between two loads is cleared, not kept. A row's
  // planning effect and its confirmation text ride along, so the effect
  // filter and quick-add see the slot's answer, not the plan's default.
  async decorate(path, signal) {
    let decorations = {}
    try {
      const response = await get(path, {responseKind: "json", signal})
      if (signal?.aborted || !response.ok) return false
      decorations = (await response.json).decorations || {}
    } catch (error) {
      return false
    }

    this.rowTargets.forEach(row => {
      const decoration = decorations[row.dataset.drawerPersonSelection]
      const badge = row.querySelector("[data-drawer-person-badge]")
      let context = row.querySelector("[data-drawer-person-context]")

      if (!decoration) {
        if (badge) {
          badge.hidden = true
          badge.textContent = ""
          badge.removeAttribute("title")
        }
        delete row.dataset.drawerPersonStatus
        delete row.dataset.drawerPersonConfirmation
        return
      }

      row.dataset.drawerPersonStatus = decoration.status
      if (decoration.effect) row.dataset.drawerPersonEffect = decoration.effect
      if (decoration.planning_values) row.dataset.drawerPersonPlanningValues = JSON.stringify(decoration.planning_values)
      if (decoration.confirmation) row.dataset.drawerPersonConfirmation = decoration.confirmation
      else delete row.dataset.drawerPersonConfirmation

      if (badge && decoration.badge) {
        badge.textContent = decoration.badge
        if (decoration.title) badge.title = decoration.title
        badge.classList.remove(...BADGE_TONES.amber, ...BADGE_TONES.slate)
        badge.classList.add(...(decoration.status === "assigned" ? BADGE_TONES.slate : BADGE_TONES.amber))
        badge.hidden = false
      }
      if (decoration.context) {
        if (!context) {
          context = document.createElement("span")
          context.dataset.drawerPersonContext = ""
          context.className = "block truncate text-[11px] leading-4 text-slate-400 dark:text-slate-400"
          row.querySelector("[data-drawer-person-name]").appendChild(context)
        }
        context.textContent = [decoration.context, row.dataset.drawerPersonSlots].filter(Boolean).join(" · ")
      }
    })

    this.filter()
    return true
  }

  // Quick-add needs rows that belong to the job the canvas has selected, and
  // the signals in -- a planning warning is confirmed the way the picker
  // confirms it, and that needs the decoration to have arrived.
  syncScope() {
    const ready = Boolean(this.renderedJob?.jobPeoplePath) && this.renderedJob === this.job && this.decorated

    this.quickAddTargets.forEach(button => {
      button.classList.toggle("hidden", !ready)
      button.classList.toggle("inline-flex", ready)
    })
  }

  async quickAdd(event) {
    const button = event.currentTarget
    const row = button.closest("[data-canvas-people-drawer-target='row']")
    const job = this.renderedJob
    if (!row || !job?.jobPeoplePath || job !== this.job) return

    const selection = this.#selectionFor(row)
    if (!selection) return

    const confirmation = row.dataset.drawerPersonConfirmation
    if (confirmation && !window.confirm(confirmation)) return

    button.disabled = true

    try {
      const response = await post(job.jobPeoplePath, {
        body: {
          staffing_plans_slots_assigned_jobs_person: {assigned_person_id_for_form: selection},
          refresh_mode: "current",
          return_to: this.returnToValue || (window.location.pathname + window.location.search)
        },
        responseKind: "turbo-stream"
      })

      if (response.ok) {
        this.unscopedDirty = true
        // The person is on the job now, so they are no longer a candidate.
        await this.load()
      } else {
        button.disabled = false
      }
    } catch (error) {
      throw error
    } finally {
      button.disabled = false
    }
  }

  // This is total plan membership, not the scoped/filtered candidate count.
  // A pool quick-add can create membership without morphing the sidebar.
  #updatePeopleCount(source) {
    const count = source.querySelector("[data-canvas-plan-people-count]")?.dataset.canvasPlanPeopleCount
    if (count === undefined) return
    this.element.closest("[data-controller~='canvas-sidebar']")
      ?.querySelectorAll("[data-canvas-sidebar-mode-count='people']")
      .forEach(badge => { badge.textContent = count })
  }

  #visible() {
    return this.hasListTarget && this.listTarget.getClientRects().length > 0
  }

  // A new scope is a new list, and its first group is the one to see:
  // whatever the previous list was scrolled to must not carry over.
  #scrollListToTop() {
    if (!this.hasListTarget) return

    const viewport = this.listTarget.closest(".canvas-sidebar-scroll-viewport")
    if (viewport) viewport.scrollTop = 0
  }

  // The picker's selection value rides on the row: an assigned-person id for
  // someone on the slot, `Person#<id>` for a plan person, `SharedPerson#<id>`
  // for a space person -- the model resolves all three.
  #selectionFor(row) {
    if (row.dataset.drawerPersonSelection) return row.dataset.drawerPersonSelection
    if (row.dataset.personId) return `Person#${row.dataset.personId}`

    return null
  }
}

// The card's staffing-pill tones: amber for anything that needs a look,
// slate for "already assigned".
const BADGE_TONES = {
  amber: ["border-amber-200", "bg-amber-50/70", "text-amber-700", "dark:border-amber-500/40", "dark:bg-amber-900/20", "dark:text-amber-200"],
  slate: ["border-slate-200/80", "bg-slate-100/80", "text-slate-500", "dark:border-slate-600", "dark:bg-slate-800/70", "dark:text-slate-300"]
}
